export const ANALYTICS_CONSENT_TTL_MS = 180 * 24 * 60 * 60 * 1000;
const CONSENT_PREFIX = "orkelys.analytics.consent.v3";
const NON_PRODUCTION_LABELS = new Set(["beta", "dev", "local", "localhost", "preview", "preprod", "staging", "test"]);
// A failed refusal write must not revive an older grant in this document.
const volatileDenials = new Map();

export const normalizeMeasurementId = value => String(value || "").trim().toUpperCase();
export const validMeasurementId = value => /^G-[A-Z0-9]{6,20}$/.test(normalizeMeasurementId(value));

export function normalizeHostname(value) {
  return String(value || "").trim().toLowerCase().replace(/\.$/, "");
}

export function validProductionHostname(value) {
  const hostname = normalizeHostname(value);
  if (!hostname || hostname.length > 253 || hostname.includes(":") || hostname.includes("/") || hostname.includes("*")) return false;
  if (!/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(hostname)) return false;
  return !hostname.split(".").some(label => NON_PRODUCTION_LABELS.has(label));
}

export function consentStorageKey(hostname, measurementId) {
  return `${CONSENT_PREFIX}:${normalizeHostname(hostname)}:${normalizeMeasurementId(measurementId)}`;
}

export function browserConsentStorage() {
  try { return window.localStorage; } catch { return null; }
}

export function readConsentRecord(storage, hostname, measurementId, now = Date.now()) {
  const key = consentStorageKey(hostname, measurementId);
  if (volatileDenials.has(key)) return volatileDenials.get(key);
  try {
    const raw = storage?.getItem(consentStorageKey(hostname, measurementId));
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!["granted", "denied"].includes(record?.choice) || !Number.isFinite(record?.expiresAt) || record.expiresAt <= now) {
      storage?.removeItem(consentStorageKey(hostname, measurementId));
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

export function readConsent(storage, hostname, measurementId, now = Date.now()) {
  return readConsentRecord(storage, hostname, measurementId, now)?.choice || "";
}

export function writeConsent(storage, hostname, measurementId, choice, now = Date.now()) {
  if (!["granted", "denied"].includes(choice)) return false;
  const key = consentStorageKey(hostname, measurementId);
  const record = { choice, expiresAt: now + ANALYTICS_CONSENT_TTL_MS };
  try {
    if (!storage) throw new Error("Storage unavailable");
    storage.setItem(key, JSON.stringify(record));
    volatileDenials.delete(key);
    return true;
  } catch {
    if (choice === "denied") volatileDenials.set(key, { choice, expiresAt: Number.MAX_SAFE_INTEGER });
    return false;
  }
}

export function analyticsRuntimeAllowed({ enabled, measurementId, configuredHostname, location, framed = false }) {
  const hostname = normalizeHostname(configuredHostname);
  const currentHostname = normalizeHostname(location?.hostname);
  if (enabled !== true || !validMeasurementId(measurementId) || !validProductionHostname(hostname)) return false;
  if (location?.protocol !== "https:" || framed || currentHostname !== hostname) return false;
  if (/^\/admin(?:\/|$)/i.test(location?.pathname || "")) return false;
  const query = new URLSearchParams(location?.search || "");
  return !query.has("site_plan_preview") && !query.has("themePreview");
}

export function analyticsPageFields(origin, pathname) {
  const path = safeAnalyticsPath(pathname);
  return { page_location: `${origin}${path}`, page_path: path, page_title: "Page publique", page_referrer: "" };
}

export function safeAnalyticsPath(pathname) {
  const path = String(pathname || "/").split(/[?#]/, 1)[0] || "/";
  if (path === "/") return "/";
  if (/^\/blog(?:\/|$)/i.test(path)) return "/blog";
  if (/^\/(?:vcard|vcard_)[^/]*/i.test(path)) return "/vcard";
  if (/^\/prendre-rendez-vous\/?$/i.test(path)) return "/prendre-rendez-vous";
  if (/^\/contact\/?$/i.test(path)) return "/contact";
  if (/^\/seances-tarifs\/?$/i.test(path)) return "/seances-tarifs";
  if (/^\/a-propos\/?$/i.test(path)) return "/a-propos";
  return "/page";
}
